import styled, { css } from "styled-components";
import { designTokens } from '../../../components/ui/foundation/DesignTokens';

// Status colors for badges
const statusColors = {
  success: { bg: '#DCFCE7', text: '#166534' },
  warning: { bg: '#FEF3C7', text: '#92400E' },
  error: { bg: '#FEE2E2', text: '#991B1B' },
  info: { bg: '#DBEAFE', text: '#1E40AF' },
};

// Teal gradient for banners
const tealGradient = 'linear-gradient(135deg, #0D9488 0%, #115E59 100%)';

// ======== LAYOUT COMPONENTS ========
export const CampaignsContainer = styled.div`
  padding: 0;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

// ======== PAGE HEADER ========
export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const PageTitle = styled.h1`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.5rem;
  color: ${designTokens.colors.gray[900]};
  margin: 0;
`;

// ======== METRICS CARDS ========
export const MetricsTop = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

export const MetricMini = styled.div`
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: 12px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: ${designTokens.shadows.sm};
`;

export const MetricIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  background: ${props => props.bg || '#EEF2FF'};
  color: ${props => props.color || '#4F46E5'};
`;

export const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MetricLabel = styled.div`
  font-size: 0.75rem;
  color: ${designTokens.colors.gray[500]};
  font-family: ${designTokens.fonts.body};
`;

export const MetricValue = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.color || designTokens.colors.gray[900]};
  font-family: ${designTokens.fonts.heading};
  display: flex;
  align-items: baseline;
  gap: 4px;

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${designTokens.colors.gray[500]};
  }
`;

// Value that could not be read, with its action next to it. The action wraps
// below the text when the tile is too narrow instead of overflowing it.
// index.css sets a font on every element with its "*" rule, which stops
// inheritance, so the font and the colour of the metric value are asked for
// explicitly: the text must look exactly like any other value of the tile.
export const MetricUnavailable = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
`;

// Text-style button, smaller than the value so the tile keeps its height.
// The padding makes the pointer target at least 24 by 24 px and the equal
// negative margin takes that padding back out of the layout: the tile does
// not grow and its neighbours do not move. The text never goes below 12 px,
// whatever the root font size is.
export const MetricRetryButton = styled.button`
  min-width: 24px;
  min-height: 24px;
  padding: 6px 8px;
  margin: -6px -8px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: max(12px, 0.8125rem);
  font-weight: 600;
  line-height: 1;
  color: ${designTokens.colors.primary[500]};
  text-decoration: underline;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: ${designTokens.colors.primary[700]};
  }

  /* Pulled in by the vertical padding, so the ring hugs the text and stays
     inside the line of the value instead of reaching the label above it. */
  &:focus-visible {
    outline: 2px solid ${designTokens.colors.primary[500]};
    outline-offset: -6px;
    /* The ring hugs the text: with the underline both lines merge into one
       band. */
    text-decoration: none;
  }
`;

// ======== CAMPAIGN CARD ========
export const CampaignCard = styled.div`
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: ${designTokens.radius.lg};
  box-shadow: ${designTokens.shadows.sm};
  padding: 24px;
  margin-bottom: 24px;
`;

export const CampaignCardHeader = styled.div`
  margin-bottom: 24px;
`;

export const CampaignCardTitle = styled.h2`
  font-family: ${designTokens.fonts.heading};
  font-size: 1.2rem;
  margin: 0 0 8px 0;
  color: ${designTokens.colors.gray[900]};
`;

export const CampaignCardDescription = styled.p`
  color: ${designTokens.colors.gray[500]};
  font-size: 0.9rem;
  margin: 0;
  font-family: ${designTokens.fonts.body};
`;

// ======== TABLE TOOLBAR ========
export const TableToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const TableToolbarLeft = styled.div`
  flex: 1;
`;

export const TableToolbarRight = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

// ======== CAMPAIGN TABLE ========
export const CampaignTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

export const CampaignTableHead = styled.thead`
  th {
    text-align: left;
    padding: 12px 16px;
    background: ${designTokens.colors.gray[50]};
    color: ${designTokens.colors.gray[500]};
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    border-bottom: 1px solid ${designTokens.colors.gray[200]};
    font-family: ${designTokens.fonts.heading};
  }
`;

export const CampaignTableBody = styled.tbody`
  td {
    padding: 16px;
    border-bottom: 1px solid ${designTokens.colors.gray[100]};
    font-size: 0.9rem;
    color: ${designTokens.colors.gray[800]};
    font-family: ${designTokens.fonts.body};
  }
`;

export const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: ${designTokens.radius.full};
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-block;
  font-family: ${designTokens.fonts.heading};

  ${props => props.status === 'pending' && css`
    background: ${statusColors.warning.bg};
    color: ${statusColors.warning.text};
  `}

  ${props => props.status === 'done' && css`
    background: ${statusColors.success.bg};
    color: ${statusColors.success.text};
  `}

  ${props => props.status === 'error' && css`
    background: ${statusColors.error.bg};
    color: ${statusColors.error.text};
  `}

  ${props => props.status === 'progress' && css`
    background: ${statusColors.info.bg};
    color: ${statusColors.info.text};
  `}
`;

export const TableLink = styled.a`
  color: ${designTokens.colors.primary[600]};
  font-weight: 600;
  font-size: 0.85rem;
  text-decoration: none;
  cursor: pointer;
  font-family: ${designTokens.fonts.heading};

  &:hover {
    text-decoration: underline;
  }
`;

// ======== WIZARD CARDS (Selection Step) ========
export const WizardHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

export const WizardTitle = styled.h1`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 2rem;
  margin-bottom: 12px;
  color: ${designTokens.colors.gray[900]};
`;

export const WizardSubtitle = styled.p`
  color: ${designTokens.colors.gray[500]};
  font-size: 1rem;
  margin: 0;
  font-family: ${designTokens.fonts.body};
`;

export const WizardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const WizardCard = styled.div`
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: 20px;
  padding: 40px 24px;
  text-align: center;
  transition: all 0.2s;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    ${props => !props.disabled && css`
      transform: translateY(-4px);
      border-color: ${designTokens.colors.primary[500]};
      box-shadow: ${designTokens.shadows.lg};
    `}
  }
`;

export const WizardIllustration = styled.div`
  width: 120px;
  height: 120px;
  margin-bottom: 24px;
  background: ${designTokens.colors.gray[50]};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;

  img {
    width: 60%;
    height: auto;
  }
`;

export const WizardCardTitle = styled.h3`
  margin: 0 0 12px 0;
  font-family: ${designTokens.fonts.heading};
  font-size: 1.1rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
`;

export const WizardCardDescription = styled.p`
  color: ${designTokens.colors.gray[500]};
  font-size: 0.9rem;
  margin-bottom: 24px;
  line-height: 1.5;
  font-family: ${designTokens.fonts.body};
`;

// ======== SPLIT LAYOUT (Form + Info) ========
export const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const InfoBox = styled.div`
  background: ${designTokens.colors.gray[50]};
  padding: 24px;
  border-radius: 16px;
  border: 1px solid ${designTokens.colors.gray[200]};
`;

export const InfoBoxTitle = styled.h3`
  margin-top: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${designTokens.fonts.heading};
  font-size: 1rem;
  color: ${designTokens.colors.gray[800]};
`;

export const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${designTokens.colors.gray[600]};
  font-size: 0.9rem;
  line-height: 1.6;
  font-family: ${designTokens.fonts.body};

  li {
    margin-bottom: 8px;
  }
`;

// ======== FORM COMPONENTS ========
export const FormLabel = styled.label`
  display: block;
  font-weight: 600;
  color: ${designTokens.colors.gray[700]};
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-family: ${designTokens.fonts.heading};
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${designTokens.colors.gray[300]};
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: ${designTokens.fonts.body};
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: ${designTokens.colors.gray[400]};
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${designTokens.colors.gray[300]};
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: ${designTokens.fonts.body};
  min-height: 150px;
  resize: vertical;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: ${designTokens.colors.gray[400]};
  }
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${designTokens.colors.gray[300]};
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: ${designTokens.fonts.body};
  box-sizing: border-box;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }
`;

export const FormHelperText = styled.p`
  font-size: 0.8rem;
  color: ${designTokens.colors.gray[400]};
  margin-top: 6px;
  margin-bottom: 0;
  font-family: ${designTokens.fonts.body};
`;

export const FormGroup = styled.div`
  margin-bottom: 24px;
`;

// ======== FILE UPLOAD ========
export const FileUploadZone = styled.div`
  border: 2px dashed ${designTokens.colors.gray[300]};
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  background: ${designTokens.colors.gray[50]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${designTokens.colors.primary[500]};
    background: ${designTokens.colors.primary[50]};
  }
`;

export const FileUploadIcon = styled.div`
  font-size: 2rem;
  color: ${designTokens.colors.gray[400]};
  margin-bottom: 12px;
`;

export const FileUploadText = styled.div`
  font-weight: 600;
  color: ${designTokens.colors.gray[700]};
  font-family: ${designTokens.fonts.heading};
`;

export const FileUploadHint = styled.div`
  font-size: 0.8rem;
  color: ${designTokens.colors.gray[500]};
  font-family: ${designTokens.fonts.body};
`;

// ======== PHONE PREVIEW ========
export const PhoneMockup = styled.div`
  background: #F0F2F5;
  border-radius: 30px;
  border: 8px solid ${designTokens.colors.gray[800]};
  padding: 20px;
  width: 320px;
  margin: 0 auto;
  min-height: 500px;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

export const PhoneHeader = styled.div`
  height: 40px;
  background: #075E54;
  margin: -20px -20px 20px -20px;
  border-radius: 20px 20px 0 0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: ${designTokens.fonts.heading};
`;

export const WaBubble = styled.div`
  background: white;
  border-radius: 0 8px 8px 8px;
  padding: 8px 10px;
  font-size: 0.85rem;
  position: relative;
  margin-bottom: 10px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
  max-width: 85%;
  font-family: ${designTokens.fonts.body};

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 0;
    border-top: 0px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid white;
  }
`;

export const WaTime = styled.span`
  font-size: 0.65rem;
  color: ${designTokens.colors.gray[400]};
  float: right;
  margin-top: 4px;
  margin-left: 8px;
`;

// ======== NAVIGATION COMPONENTS ========
export const BackLink = styled.div`
  cursor: pointer;
  color: ${designTokens.colors.gray[500]};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: ${designTokens.fonts.heading};
  transition: color 0.2s ease;

  &:hover {
    color: ${designTokens.colors.primary[500]};
  }
`;

export const ActionButtons = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

// ======== BANNER ========
export const CampaignBanner = styled.div`
  padding: 32px 40px;
  border-radius: ${designTokens.radius.lg};
  color: white;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
  box-shadow: ${designTokens.shadows.sm};
  background: ${tealGradient};
`;

export const BannerContent = styled.div`
  position: relative;
  z-index: 1;
`;

export const BannerTitle = styled.h1`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.75rem;
  margin: 0 0 8px 0;
`;

export const BannerDescription = styled.p`
  font-size: 0.95rem;
  opacity: 0.95;
  line-height: 1.5;
  margin: 0;
  font-family: ${designTokens.fonts.body};
`;

export const BannerSVG = styled.svg`
  position: absolute;
  right: -20px;
  bottom: -40px;
  width: 220px;
  height: 220px;
  opacity: 0.15;
  fill: currentColor;
  stroke: currentColor;
  stroke-width: 2;
  transform: rotate(-10deg);
`;

// ======== VARIABLE TAGS ========
export const VariableTag = styled.span`
  background: ${designTokens.colors.gray[100]};
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.85rem;
`;

// ======== AI ENHANCE BUTTON ========
export const AIEnhanceButton = styled.div`
  font-size: 0.8rem;
  color: ${designTokens.colors.primary[600]};
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: ${designTokens.fonts.heading};

  &:hover {
    color: ${designTokens.colors.primary[500]};
  }
`;

// ======== CHECKBOX LABEL ========
export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: ${designTokens.fonts.body};
  font-size: 0.9rem;
  color: ${designTokens.colors.gray[700]};

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;
